import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Mail, Send, Phone, ChevronDown, HelpCircle } from "lucide-react";
import axios from "axios";
import { trackContact } from "@/utils/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const faqs = [
  {
    question: "What types of music do you record?",
    answer: "We are equipped to record all genres of music, including rock, pop, hip-hop, classical, and more."
  },
  {
    question: "What is your approach to recording?",
    answer: "We believe in capturing the essence of your music. Our professional team works with you to ensure your vision is realised through state-of-the-art recording equipment and a collaborative process."
  },
  {
    question: "Do you offer mixing and mastering services?",
    answer: "Yes, we offer professional mixing and mastering services to enhance the sound and quality of your recording."
  },
  {
    question: "What sets you apart from other studios?",
    answer: "We believe in fostering creativity and innovation through a collaborative process. Our state-of-the-art facilities and attention to detail ensure every recording is of the highest quality."
  },
  {
    question: "Do you provide photography services?",
    answer: "Yes, we provide professional photography services to capture your music, brand, and identity. Our team can help you create stunning images for album covers, social media, and promotional materials."
  },
  {
    question: "What is the studio's availability?",
    answer: "Our studio is available for booking seven days a week, 24 hours a day. Contact us to schedule a consultation and discuss availability."
  },
  {
    question: "Do you offer any discounts or promotions?",
    answer: "We occasionally offer promotions and discounts to our clients. Follow us on social media or contact us to learn more about our current offers."
  },
  {
    question: "I want to start a song from scratch, how does this work?",
    answer: "1. The first step is making the beat. This means creating the music part of a song using drums, bass, melodies, and more. We use special tools to layer and arrange sounds to create a strong rhythm for any type of music.\n\n2. The second step is recording. This is when we record your voice with a microphone to get the best, high-quality sound for your song.\n\n3. The third step is mixing. Here, we put together all the recorded parts and adjust the volume, effects, and sound placement to make everything sound balanced and clear.\n\n4. The last step is mastering. This final touch makes sure your song sounds great on any device. We adjust the sound quality, volume, and overall feel to meet industry standards."
  },
  {
    question: "How long will my mix and master take to be completed?",
    answer: "Generally your final mix and master will return back to your emails 1-2 days max after your session. You will still go home with a rough copy to hear back on what you've completed."
  },
  {
    question: "Do you work online?",
    answer: "Our engineers are equipped to work online with completing your mix and masters. Easy as sending your files over and will take care of the rest!"
  },
  {
    question: "Can I go on a payment plan?",
    answer: "Yes! What sets us apart at DMRG will conveniently offer Afterpay and Zip Pay to take the stress off your creative process. Record today, pay later!"
  },
  {
    question: "How do I make payment?",
    answer: "Payments can be made directly through our website or in person."
  }
];

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-dream-border last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-start justify-between gap-4 text-left"
      >
        <span className="font-medium text-white pr-4">{faq.question}</span>
        <ChevronDown 
          className={`w-5 h-5 text-dream-violet flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-5 text-white/60 whitespace-pre-line">{faq.answer}</p>
      </motion.div>
    </div>
  );
}

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      });

      // Track contact form submission with Meta Pixel
      trackContact();
      
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ fullName: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Contact</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-white/60 text-lg">
              Have questions about our services? Want to discuss a project? 
              We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-dream-surface">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8">
                Visit Our Studio
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-dream-violet/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-dream-violet" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <a href="mailto:dreammakersrecordinggroup@gmail.com" className="text-white/60 hover:text-dream-violet transition-colors">
                      dreammakersrecordinggroup@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-dream-violet/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-dream-violet" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Phone</h3>
                    <a href="tel:0421815593" className="text-white/60 hover:text-dream-violet transition-colors block">
                      0421 815 593
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-dream-violet/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-dream-violet" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Studio Hours</h3>
                    <div className="text-white/60 text-sm space-y-1">
                      <p><span className="text-white">Recording Studio:</span> Mon-Thu 5PM-12AM, Fri-Sun 9AM-2AM</p>
                      <p><span className="text-white">Production Room:</span> Same as Recording Studio</p>
                      <p><span className="text-white">DJ Room:</span> 12PM-12AM, 7 days a week</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
            >
              <div className="glass rounded-2xl p-8">
                <h2 className="font-heading text-2xl font-bold text-white mb-6">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label className="text-white/60 mb-2 block">Full Name *</Label>
                    <Input
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder="John Smith"
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30 h-12"
                      data-testid="contact-input-name"
                    />
                  </div>

                  <div>
                    <Label className="text-white/60 mb-2 block">Email Address *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30 h-12"
                      data-testid="contact-input-email"
                    />
                  </div>

                  <div>
                    <Label className="text-white/60 mb-2 block">Phone Number</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="0400 000 000"
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30 h-12"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div>
                    <Label className="text-white/60 mb-2 block">Message *</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your project or ask any questions..."
                      rows={5}
                      className="bg-dream-surface border-dream-border text-white placeholder:text-white/30 resize-none"
                      data-testid="contact-input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-dream-violet text-white hover:bg-dream-violet-glow rounded-full py-6"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-dream-bg" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-dream-violet/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-dream-violet" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-white/60">
              Find answers to common questions about our studio and services.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="glass rounded-2xl p-6 md:p-8"
          >
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
